
import React from "react";
import { Link } from "react-router-dom";
import { LogIn, Smartphone } from "lucide-react";
import { getAssetPath } from "@/utils/assetUtils";

interface VerificationLayoutProps {
  email: string;
  children: React.ReactNode;
}

export const VerificationLayout = ({ email, children }: VerificationLayoutProps) => {
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-black p-4">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="text-white flex items-center">
            <img 
              src={getAssetPath("disco_logo.png")} 
              alt="Disco Logo" 
              className="h-8" 
              onError={(e) => {
                console.error("Failed to load logo in VerificationLayout");
                // Fallback to text if image fails to load
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.insertAdjacentHTML('afterbegin', '<span class="font-bold text-xl">DISCO</span>');
              }}
            />
          </Link>
          <Link 
            to="/login" 
            className="text-white flex items-center gap-1 text-sm hover:opacity-80 transition-opacity"
          >
            <LogIn size={16} />
            Log in
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-lg shadow-sm w-full max-w-md p-8">
          <div className="flex justify-center mb-6">
            <div className="h-12 w-12 rounded-full bg-black flex items-center justify-center">
              <Smartphone className="h-6 w-6 text-green-400" />
            </div>
          </div>
          
          <h1 className="text-center text-2xl font-normal mb-2 text-black">
            Verify your account
          </h1>
          
          <p className="text-center text-gray-600 mb-8">
            We've sent a 6-digit code to your phone.
            {email && <span className="block mt-1">Associated with: {email}</span>}
          </p>
          
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black text-white py-3 px-4 text-xs">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div>©2025 Disco. All Rights Reserved</div>
          <div className="flex gap-3">
            <a 
              href="https://www.discoveryconciergeservices.com/terms-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms & Conditions
            </a>
            <span>|</span>
            <a 
              href="https://www.discoveryconciergeservices.com/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
